import { ArrowRight, Crosshair } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { ApiError } from "@/core/api/types";
import { cn } from "@/core/utils/utils";
import { formatPersistentEditValue } from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import type { IAiSpreadsheetCellLocation } from "@/payroll/ai-script/types";

export const showPersistentEditMutationError = (
  message: string,
  error: unknown,
): boolean => {
  if (error instanceof ApiError && error.status === 403) {
    toast.error("You don't have permission to review this edit", {
      description:
        "Your Grid access may have changed. The available edits are being refreshed.",
    });
    return true;
  }

  toast.error(message, {
    description: "Something went wrong — please try again.",
  });
  return false;
};

export const SectionLabel = ({ children }: { children: ReactNode }) => (
  <Typography variant="subtitleW2">{children}</Typography>
);

// The card shell for one edit. Matches the activity feed's list cards — the
// app's vocabulary for "a list of things that happened, some of which you can
// act on".
export const EditCard = ({
  children,
  onClick,
  ...props
}: ComponentProps<typeof Card>) => (
  <Card
    className={cn(
      "w-full min-w-0 overflow-hidden rounded-2xl border-stroke-tertiary p-4 shadow-none",
      {
        "cursor-pointer transition-colors hover:bg-bg-secondary/50": !!onClick,
      },
    )}
    onClick={onClick}
    {...props}
  >
    <Stack gap="sm" fullWidth align="stretch" className="min-w-0">
      {children}
    </Stack>
  </Card>
);

const hasValue = (value: string | number | boolean | null | undefined) =>
  value !== null && value !== undefined && value !== "";

// An edit is a change, so show it as one — "34.25 → 34" says more than either
// number alone. Cells that were blank before the edit have nothing to compare
// against, so they just show what they hold now.
export const EditValueChange = ({
  from,
  to,
}: {
  from: string | number | boolean | null | undefined;
  to: string | number | boolean | null | undefined;
}) => (
  <Stack direction="row" gap="sm" align="center" fullWidth className="min-w-0">
    {hasValue(from) ? (
      <>
        <Typography
          variant="bodyW1"
          textColor="tertiary"
          className="min-w-0 max-w-[45%] shrink-0 truncate"
        >
          {formatPersistentEditValue(from)}
        </Typography>
        <ArrowRight className="h-4 w-4 shrink-0 text-tertiary-text" />
      </>
    ) : null}
    <Typography variant="bodyW1" className="min-w-0 flex-1 font-semibold">
      <PersistentEditValue value={to} />
    </Typography>
  </Stack>
);

export const BoldValue = ({ children }: { children: ReactNode }) => (
  <span className="font-semibold text-primary-text">{children}</span>
);

export const PersistentEditValue = ({
  value,
}: {
  value: string | number | boolean | null | undefined;
}) => {
  const displayValue = formatPersistentEditValue(value);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* inline-block so the value can sit inside a sentence ("Your edit (12)
            matched…") as well as stand alone as a card's headline. */}
        <span
          className="inline-block min-w-0 max-w-full truncate align-bottom font-semibold text-primary-text"
          data-persistent-edit-value
        >
          {displayValue}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm [overflow-wrap:anywhere]">
        {displayValue}
      </TooltipContent>
    </Tooltip>
  );
};

export const CellLocationLink = ({
  cell,
  onGoToCell,
}: {
  cell: IAiSpreadsheetCellLocation | null | undefined;
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
}) => {
  if (!cell) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onGoToCell(cell);
          }}
          className="inline-flex min-w-0 items-center gap-1.5 rounded text-md font-semibold text-primary-text transition-colors hover:text-interactive-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Crosshair className="h-4 w-4 shrink-0 text-tertiary-text" />
          <span className="truncate">
            {cell.sheet_name
              ? `${cell.sheet_name} · ${cell.address}`
              : cell.address}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent>Go to cell</TooltipContent>
    </Tooltip>
  );
};

export const LoadError = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) => (
  <Stack gap="sm" align="start" fullWidth>
    <Typography variant="bodyW3" textColor="secondary">
      {message}
    </Typography>
    <Button variant="secondary" size="sm" onClick={onRetry}>
      Try again
    </Button>
  </Stack>
);
